import * as THREE from 'three';

// Camera follows behind/above the local racer.
export class Camera {
  constructor() {
    this.camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000,
    );
    this.camera.position.set(0, 80, 60);
    this.camera.lookAt(0, 0, 0);

    window.addEventListener('resize', () => this.handleResize());
  }

  handleResize() {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }

  // Smoothly chase the target, sitting behind and above. The racer's forward
  // direction is (sin yaw, 0, -cos yaw), so "behind" is the negation:
  // (-sin yaw, 0, +cos yaw) → camera = target + behind * distance + up * height.
  //
  // viewYawOffset (radians) orbits the camera around the racer without
  // changing the racer's heading — used by the touch 2-finger pan gesture
  // so you can look around (e.g. look back at chasers).
  follow(targetPos, yaw, dt, viewYawOffset = 0) {
    if (!targetPos) return;
    const distance = 8;
    const height = 4;
    const effectiveYaw = yaw + viewYawOffset;
    const desired = new THREE.Vector3(
      targetPos.x - Math.sin(effectiveYaw) * distance,
      targetPos.y + height,
      targetPos.z + Math.cos(effectiveYaw) * distance,
    );
    const k = 1 - Math.exp(-dt * 6);
    this.camera.position.lerp(desired, k);
    this.camera.lookAt(targetPos.x, targetPos.y + 1.2, targetPos.z);
  }

  // High overhead chase used by the V-key view toggle. Sits well above and
  // a bit further back than the default chase cam so the rest of the field
  // is visible ahead/behind. Aimed at the racer with a steeper downward tilt.
  followOverhead(targetPos, yaw, dt, viewYawOffset = 0) {
    if (!targetPos) return;
    const distance = 14;
    const height = 22;
    const effectiveYaw = yaw + viewYawOffset;
    const desired = new THREE.Vector3(
      targetPos.x - Math.sin(effectiveYaw) * distance,
      targetPos.y + height,
      targetPos.z + Math.cos(effectiveYaw) * distance,
    );
    const k = 1 - Math.exp(-dt * 6);
    this.camera.position.lerp(desired, k);
    this.camera.lookAt(targetPos.x, targetPos.y, targetPos.z);
  }

  // TV-broadcast spectator cam: positioned 30° above the racer, off to the
  // side (perpendicular to their forward direction), looking across them.
  // Gives a side-on profile of the leader rather than a behind-the-back chase.
  followSpectator(targetPos, yaw, dt) {
    if (!targetPos) return;
    const sideDistance = 14;
    const height = 8;
    // Perpendicular to forward (sin yaw, 0, -cos yaw) → side = (cos yaw, 0, sin yaw)
    const desired = new THREE.Vector3(
      targetPos.x + Math.cos(yaw) * sideDistance,
      targetPos.y + height,
      targetPos.z + Math.sin(yaw) * sideDistance,
    );
    const k = 1 - Math.exp(-dt * 4);
    this.camera.position.lerp(desired, k);
    this.camera.lookAt(targetPos.x, targetPos.y + 1.0, targetPos.z);
  }

  // Smoothly settle behind a fixed focus point and look at it — used during
  // the race-over podium ceremony so the camera ends up framing the winners.
  // The camera sits a little higher and the look-at point sits a little
  // lower than the podium centre so all three figures land in the upper
  // ~70% of the screen, clear of the results panel at the bottom.
  framePodium(focus, dt) {
    if (!focus) return;
    const desired = new THREE.Vector3(focus.x, focus.y + 4.5, focus.z + 13);
    const k = 1 - Math.exp(-dt * 3);
    this.camera.position.lerp(desired, k);
    this.camera.lookAt(focus.x, focus.y - 0.5, focus.z);
  }

  getCamera() {
    return this.camera;
  }
}
